import { getCookie } from '@/utils/cookies';
import { getMicroservicesClient } from '@/services/microservices/client';
import type { MicroservicesCountryCode } from './usedCarEligibility';

interface AuthTokenCookieValue {
    grant?: string;
    identityGrant?:
        | {
              grant?: string;
          }
        | string;
}

interface LegacySessionClient {
    user: {
        getUserInfo: () => {
            identityGrant?: {
                grant?: string;
            };
        };
    };
}

interface EnsureIdentityGrantSessionOptions {
    allowExistingLegacySession?: boolean;
}

interface MicroserviceAuthError {
    status?: number;
    code?: string;
    [key: string]: unknown;
}

const IDENTITY_GRANT_EXPIRY_BUFFER_MS = 30_000;
const pendingLegacyLogins = new Map<MicroservicesCountryCode, Promise<void>>();

export class IdentityGrantUnavailableError extends Error {
    constructor() {
        super('A current identity grant is required to restore the SMS legacy session');
        this.name = 'IdentityGrantUnavailableError';
    }
}

export class LegacySessionExpiredError extends Error {
    constructor() {
        super('The SMS legacy account session has expired');
        this.name = 'LegacySessionExpiredError';
    }
}

function getJwtExpirationMs(token: string): number | null {
    const encodedPayload = token.split('.')[1];

    if (!encodedPayload) {
        return null;
    }

    try {
        const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        const payload = JSON.parse(atob(paddedBase64)) as { exp?: unknown };

        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

export function isIdentityGrantUsable(identityGrant: string, now = Date.now()) {
    const expirationMs = getJwtExpirationMs(identityGrant);

    return expirationMs != null && expirationMs > now + IDENTITY_GRANT_EXPIRY_BUFFER_MS;
}

async function readJsonSafely(response: Response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

async function logoutIdentityGrantSession(client: ReturnType<typeof getMicroservicesClient>) {
    await fetch(`${client.baseUrl}/authenticate/logout`, {
        method: 'POST',
        headers: {
            Accept: 'application/json; charset=utf-8',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: 'PHX' }),
        credentials: 'include',
    }).catch(() => undefined);
}

function buildMicroserviceAuthError(response: Response, json: unknown): MicroserviceAuthError {
    if (json && typeof json === 'object') {
        const candidate = 'error' in json && json.error && typeof json.error === 'object' ? json.error : json;

        return {
            status: response.status,
            ...(typeof candidate === 'object' && candidate ? candidate : {}),
        };
    }

    return {
        status: response.status,
        code: response.statusText || 'authenticate_login_failed',
    };
}

export async function loginWithIdentityGrant(countryCode: MicroservicesCountryCode, identityGrant: string) {
    const client = getMicroservicesClient(countryCode);

    if (!client.baseUrl) {
        throw new Error(
            `Missing microservices base URL for ${countryCode.toUpperCase()}. Check the runtime configuration for the active environment.`,
        );
    }

    try {
        const response = await fetch(`${client.baseUrl}/authenticate/login`, {
            method: 'POST',
            headers: {
                Accept: 'application/json; charset=utf-8',
                Authorization: `Bearer ${identityGrant}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
            credentials: 'include',
        });
        const json = await readJsonSafely(response);

        if (!response.ok) {
            if (response.status >= 500) {
                await logoutIdentityGrantSession(client);
            }

            return Promise.reject(buildMicroserviceAuthError(response, json));
        }

        return json;
    } catch (error) {
        // If the SMS bridge fails at the transport/gateway layer, clear any
        // partially restored authenticated session before surfacing the error.
        try {
            await logoutIdentityGrantSession(client);
        } catch {}

        return Promise.reject(error);
    }
}

export async function logoutWithIdentityGrantSession(countryCode: MicroservicesCountryCode) {
    const client = getMicroservicesClient(countryCode);

    if (!client.baseUrl) {
        return;
    }

    await logoutIdentityGrantSession(client);
}

function hasUsableLegacyAccessCookie() {
    const cookie = getCookie('SXM_C_A');

    return Boolean(cookie && isIdentityGrantUsable(cookie));
}

function getCurrentIdentityGrant(clientSdk: LegacySessionClient) {
    const currentGrant = clientSdk.user.getUserInfo().identityGrant?.grant ?? getAuthTokenIdentityGrant();

    if (currentGrant && isIdentityGrantUsable(currentGrant)) {
        return currentGrant;
    }

    return null;
}

export function ensureIdentityGrantSession(
    clientSdk: LegacySessionClient,
    countryCode: MicroservicesCountryCode,
    options: EnsureIdentityGrantSessionOptions = {},
): Promise<void> {
    const pendingLogin = pendingLegacyLogins.get(countryCode);

    if (pendingLogin) {
        return pendingLogin;
    }

    const login = (async () => {
        if (options.allowExistingLegacySession) {
            if (hasUsableLegacyAccessCookie()) {
                return;
            }

            throw new LegacySessionExpiredError();
        }

        const identityGrant = getCurrentIdentityGrant(clientSdk);

        if (identityGrant) {
            await loginWithIdentityGrant(countryCode, identityGrant);
            return;
        }

        throw new IdentityGrantUnavailableError();
    })();
    const trackedLogin = login.finally(() => {
        if (pendingLegacyLogins.get(countryCode) === trackedLogin) {
            pendingLegacyLogins.delete(countryCode);
        }
    });

    pendingLegacyLogins.set(countryCode, trackedLogin);

    return trackedLogin;
}

export function getAuthTokenIdentityGrant(): string | null {
    const authTokenCookie = getCookie('AUTH_TOKEN');

    if (!authTokenCookie) {
        return null;
    }

    try {
        const parsedCookie = JSON.parse(authTokenCookie) as AuthTokenCookieValue;

        if (typeof parsedCookie.identityGrant === 'string') {
            return parsedCookie.identityGrant;
        }

        if (typeof parsedCookie.identityGrant?.grant === 'string') {
            return parsedCookie.identityGrant.grant;
        }

        if (typeof parsedCookie.grant === 'string') {
            return parsedCookie.grant;
        }
    } catch {}

    return null;
}
